/**
 * Sync Manager
 *
 * Orchestrates the synchronization of offline mutations with the server.
 * Uses batch sync to send all pending mutations in a single request.
 *
 * IMPORTANT: Files are synced BEFORE mutations to ensure that uploaded photo URLs
 * are available to be injected into CREATE mutations.
 */

import { QueryClient } from 'react-query';

import { authenticationState } from 'app/authentication/state';
import { subscribeToSyncMessages } from './backgroundSync';
import { sendBatchSync } from './batchSyncApi';
import { fileSyncManager } from './fileSyncManager';
import {
  cleanupInvalidMutations,
  getMutationQueue,
  getPendingMutationCount,
  getPendingMutations,
  recoverStuckMutations,
  removeMutation,
  updateMutationStatus,
} from './mutationQueue';
import { persistQueryClient } from './reactQueryPersistence';
import { MutationOperation, SyncEvent, SyncProgress, SyncResult } from './types';
import { isCurrentlyOnline, subscribeOnlineStatus } from './useOnlineStatus';
import { flushLocalToRemoteUrlMappings } from './uploadedPhotoRegistry';

/** Maximum number of retries for batch sync */
const MAX_BATCH_RETRIES = 3;

/** Base delay between retries in milliseconds */
const RETRY_BASE_DELAY = 1000;

/** Maximum delay between retries in milliseconds */
const RETRY_MAX_DELAY = 30000;

type SyncEventCallback = (event: SyncEvent) => void;

interface SyncManagerOptions {
  /** Disable retry delays for testing */
  disableRetryDelay?: boolean;
}

/**
 * SyncManager class handles the orchestration of offline mutation syncing
 */
class SyncManager {
  private isSyncing = false;

  private eventListeners: Set<SyncEventCallback> = new Set();

  private unsubscribeOnlineStatus: (() => void) | null = null;

  private unsubscribeSyncMessages: (() => void) | null = null;

  private queryClient: QueryClient | null = null;

  private options: SyncManagerOptions = {};

  /**
   * Initialize the sync manager with a QueryClient
   * Should be called once when the app starts
   */
  initialize(queryClient: QueryClient, options: SyncManagerOptions = {}): void {
    this.queryClient = queryClient;
    this.options = options;

    // Clean up any mutations with invalid format (from old code versions)
    cleanupInvalidMutations();

    // Recover any mutations stuck in 'syncing' state (from app crash/close)
    recoverStuckMutations();

    // Subscribe to online status changes
    this.unsubscribeOnlineStatus = subscribeOnlineStatus((isOnline) => {
      if (isOnline) {
        this.startSync();
      }
    });

    // Subscribe to sync messages from service worker (background sync)
    this.unsubscribeSyncMessages = subscribeToSyncMessages(() => {
      if (isCurrentlyOnline()) {
        this.startSync();
      }
    });

    // Check if we should sync on initialization
    if (isCurrentlyOnline()) {
      this.startSync();
    }
  }

  /**
   * Clean up subscriptions
   */
  destroy(): void {
    if (this.unsubscribeOnlineStatus) {
      this.unsubscribeOnlineStatus();
      this.unsubscribeOnlineStatus = null;
    }
    if (this.unsubscribeSyncMessages) {
      this.unsubscribeSyncMessages();
      this.unsubscribeSyncMessages = null;
    }
    this.eventListeners.clear();
    this.queryClient = null;
  }

  /**
   * Subscribe to sync events
   */
  subscribe(callback: SyncEventCallback): () => void {
    this.eventListeners.add(callback);
    return () => {
      this.eventListeners.delete(callback);
    };
  }

  /**
   * Emit a sync event to all listeners
   */
  private emit(event: SyncEvent): void {
    this.eventListeners.forEach((callback) => {
      try {
        callback(event);
      } catch {
        // Listener error - ignore
      }
    });
  }

  /**
   * Check if currently syncing
   */
  getIsSyncing(): boolean {
    return this.isSyncing;
  }

  /**
   * Start the sync process
   * Sends all pending mutations as a single batch request
   *
   * IMPORTANT: Sync order matters for photos:
   * 1. Area object photos ('object' uploadType) are synced FIRST so their URLs can be
   *    injected into CREATE mutations via uploadedPhotoRegistry.
   * 2. Mutations are then processed (including CREATE mutations with photo URLs).
   * 3. Outlet photos ('outlet', 'tpOutlet' uploadType) are synced LAST because they
   *    require the outlets to exist on the server before the uploadOutletPhoto mutation.
   */
  async startSync(): Promise<void> {
    if (this.isSyncing) {
      return;
    }

    if (!isCurrentlyOnline()) {
      return;
    }

    // Don't sync if we don't have an auth token yet (app still loading)
    const { token } = authenticationState();
    if (!token) {
      return;
    }

    // STEP 1: Sync ONLY 'object' type files (area photos) FIRST
    // These need to be uploaded before mutations so their URLs can be injected
    try {
      await fileSyncManager.startSync(['object']);
    } catch {
      // Step 1 failed, proceeding with mutations
    }

    // STEP 2: Process mutations
    const pendingMutations = await getPendingMutations();

    if (pendingMutations.length === 0) {
      // No mutations, but still sync outlet photos if any
      try {
        await fileSyncManager.startSync(['outlet', 'tpOutlet']);
      } catch {
        // Failed to sync outlet photos - ignore
      }
      return;
    }

    this.isSyncing = true;

    const progress: SyncProgress = {
      total: pendingMutations.length,
      completed: 0,
      failed: 0,
    };

    this.emit({ type: 'SYNC_STARTED', payload: { total: progress.total } });

    try {
      // Mark all mutations as syncing
      // eslint-disable-next-line no-restricted-syntax
      for (const mutation of pendingMutations) {
        // eslint-disable-next-line no-await-in-loop
        await updateMutationStatus(mutation.id, 'syncing');
      }

      // Send all mutations as a single batch request
      const batchResult = await this.sendBatchWithRetry(pendingMutations);

      // Process batch results
      // eslint-disable-next-line no-restricted-syntax
      for (const result of batchResult.results) {
        const mutation = pendingMutations.find((m) => m.id === result.mutationId);

        if (result.success) {
          progress.completed += 1;
          // Remove successful mutation from queue
          // eslint-disable-next-line no-await-in-loop
          await removeMutation(result.mutationId);
          this.emit({ type: 'MUTATION_SYNCED', payload: { mutationId: result.mutationId } });

          // Refetch related queries if specified (use refetchQueries instead of invalidateQueries
          // to ensure data is actually fetched from server, not just marked stale)
          if (this.queryClient && mutation?.invalidateKeys) {
            // eslint-disable-next-line no-restricted-syntax
            for (const key of mutation.invalidateKeys) {
              // eslint-disable-next-line no-await-in-loop
              await this.queryClient.refetchQueries(key);
            }
          }
        } else {
          progress.failed += 1;
          // Mark failed mutation with error
          // eslint-disable-next-line no-await-in-loop
          await updateMutationStatus(result.mutationId, 'failed', result.error ?? 'Unknown error');
          this.emit({
            type: 'SYNC_ERROR',
            payload: { mutationId: result.mutationId, error: result.error ?? 'Unknown error' },
          });
        }
      }
    } catch (error) {
      // Batch sync completely failed - mark all as failed
      const errorMessage = error instanceof Error ? error.message : 'Batch sync failed';

      // eslint-disable-next-line no-restricted-syntax
      for (const mutation of pendingMutations) {
        progress.failed += 1;
        // eslint-disable-next-line no-await-in-loop
        await updateMutationStatus(mutation.id, 'failed', errorMessage);
        this.emit({
          type: 'SYNC_ERROR',
          payload: { mutationId: mutation.id, error: errorMessage },
        });
      }
    } finally {
      this.isSyncing = false;

      // Persist the updated query cache to IndexedDB so refreshed pages get fresh data
      if (this.queryClient && progress.completed > 0) {
        try {
          await persistQueryClient(this.queryClient);
        } catch {
          // Failed to persist - ignore
        }
      }

      this.emit({
        type: 'SYNC_COMPLETED',
        payload: { successful: progress.completed, failed: progress.failed },
      });

      // STEP 3: Sync outlet/tpOutlet photos AFTER mutations have been processed
      // These require outlets to exist on the server first
      try {
        await fileSyncManager.startSync(['outlet', 'tpOutlet']);
      } catch {
        // Step 3 failed - ignore
      }

      if ((await getPendingMutationCount()) === 0) {
        flushLocalToRemoteUrlMappings();
      }
    }
  }

  /**
   * Send batch with retry logic
   */
  private async sendBatchWithRetry(
    mutations: MutationOperation[],
    retryCount = 0,
  ): Promise<{ results: Array<{ mutationId: string; success: boolean; error?: string }> }> {
    try {
      const response = await sendBatchSync(mutations);

      return {
        results: response.results.map((result) => ({
          mutationId: result.mutationId,
          success: result.success,
          error: result.error,
        })),
      };
    } catch (error) {
      if (retryCount < MAX_BATCH_RETRIES && this.isRetryableError(error)) {
        // Calculate delay with exponential backoff (skip in test mode)
        if (!this.options.disableRetryDelay) {
          const delay = Math.min(RETRY_BASE_DELAY * 2 ** retryCount, RETRY_MAX_DELAY);
          await this.sleep(delay);
        }
        return this.sendBatchWithRetry(mutations, retryCount + 1);
      }
      throw error;
    }
  }

  /**
   * Process a single mutation (used for force sync)
   */
  private async processSingleMutation(mutation: MutationOperation): Promise<SyncResult> {
    // Mark as syncing
    await updateMutationStatus(mutation.id, 'syncing');

    try {
      // Send as a single-item batch
      const response = await sendBatchSync([mutation]);

      const result = response.results[0];

      if (result?.success) {
        // Success - remove from queue
        await removeMutation(mutation.id);

        // Refetch related queries if specified (use refetchQueries instead of invalidateQueries
        // to ensure data is actually fetched from server)
        if (this.queryClient && mutation.invalidateKeys) {
          // eslint-disable-next-line no-restricted-syntax
          for (const key of mutation.invalidateKeys) {
            // eslint-disable-next-line no-await-in-loop
            await this.queryClient.refetchQueries(key);
          }

          // Persist the updated cache
          await persistQueryClient(this.queryClient);
        }

        return {
          success: true,
          mutationId: mutation.id,
          serverResponse: result.data,
        };
      }
      await updateMutationStatus(mutation.id, 'failed', result?.error ?? 'Unknown error');
      return {
        success: false,
        mutationId: mutation.id,
        error: result?.error ?? 'Unknown error',
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      await updateMutationStatus(mutation.id, 'failed', errorMessage);

      return {
        success: false,
        mutationId: mutation.id,
        error: errorMessage,
      };
    }
  }

  /**
   * Check if an error is retryable
   */
  // eslint-disable-next-line class-methods-use-this
  private isRetryableError(error: unknown): boolean {
    if (error instanceof Error) {
      const message = error.message.toLowerCase();

      // Network errors are retryable
      if (message.includes('network') || message.includes('fetch') || message.includes('timeout')) {
        return true;
      }

      // 5xx server errors are retryable
      if (message.includes('500') || message.includes('502') || message.includes('503') || message.includes('504')) {
        return true;
      }

      // 4xx client errors are generally not retryable
      if (message.includes('400') || message.includes('401') || message.includes('403') || message.includes('404')) {
        return false;
      }
    }

    // Default to retryable
    return true;
  }

  /**
   * Sleep for a given duration
   */
  // eslint-disable-next-line class-methods-use-this
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }

  /**
   * Force sync a specific mutation (bypass normal queue)
   */
  async forceSyncMutation(mutationId: string): Promise<SyncResult> {
    const queue = await getMutationQueue();
    const mutation = queue.find((m) => m.id === mutationId);

    if (!mutation) {
      return {
        success: false,
        mutationId,
        error: 'Mutation not found',
      };
    }

    if (!isCurrentlyOnline()) {
      return {
        success: false,
        mutationId,
        error: 'Currently offline',
      };
    }

    return this.processSingleMutation(mutation);
  }

  /**
   * Get current sync status
   */
  async getSyncStatus(): Promise<{
    isSyncing: boolean;
    pendingCount: number;
    failedCount: number;
  }> {
    const queue = await getMutationQueue();
    const pending = queue.filter((m) => m.status === 'pending' || m.status === 'syncing');
    const failed = queue.filter((m) => m.status === 'failed');

    return {
      isSyncing: this.isSyncing,
      pendingCount: pending.length,
      failedCount: failed.length,
    };
  }

  /**
   * Discard all local changes and reset to server state
   *
   * This is an irreversible operation that:
   * 1. Clears all pending mutations from the queue
   * 2. Clears all pending file uploads
   * 3. Clears all local photo references
   * 4. Restores the React Query cache from persisted server snapshot
   *
   * Works in both online and offline mode - doesn't require network.
   *
   * @returns Promise that resolves when discard is complete
   */
  async discardLocalChanges(): Promise<void> {
    // Import dynamically to avoid circular dependencies
    const { clearAllFileUploads } = await import('./fileStorage');
    const { clearAllLocalPhotos } = await import('./offlinePhotoStorage');
    const { clearMutationQueue } = await import('./mutationQueue');
    const { resetQueryCacheToPersistedSnapshot } = await import('./reactQueryPersistence');

    // Step 1: Clear all pending mutations
    await clearMutationQueue();

    // Step 2: Clear all pending file uploads
    await clearAllFileUploads();

    // Step 3: Clear all local photo references (in-memory blob URLs)
    clearAllLocalPhotos();

    // Step 4: Reset in-memory query cache to persisted server snapshot
    // This uses setQueryData which doesn't trigger refetches (unlike clear + hydrate)
    // Don't clear the persisted cache - it contains the original server data
    if (this.queryClient) {
      await resetQueryCacheToPersistedSnapshot(this.queryClient);
    }

    // Emit a custom event to notify UI components
    if (typeof window !== 'undefined') {
      const event = new CustomEvent('offlineChangesDiscarded');
      window.dispatchEvent(event);
    }
  }
}

// Export singleton instance
export const syncManager = new SyncManager();

// Export class for testing purposes
export { SyncManager };
